import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    MenuItem,
    Skeleton,
    TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import {
    EXPERIMENT_MISSION_PROCESSING_STATUS,
    INPUT_VALIDATION,
} from "../../constants";
import { useFetch, useSnackbar } from "../../hooks";

import { BACKEND_ROUTES } from "../../backendRoutes";
import { FetchErrorAlert } from "../../components/FetchErrorAlert";
import { LoadingButton } from "@mui/lab";
import PropTypes from "prop-types";
import { formatDateTimeUTC } from "../../utils/formatTime";
import { useEffect } from "react";
import useSWR from "swr";

const processingStatusValues = Object.values(
    EXPERIMENT_MISSION_PROCESSING_STATUS
);

ExperimentMissionModal.propTypes = {
    experimentMissionUuid: PropTypes.string,
    closeModal: PropTypes.func.isRequired,
    mutateTable: PropTypes.func.isRequired,
};

export function ExperimentMissionModal({
    experimentMissionUuid,
    closeModal,
    mutateTable,
}) {
    const { patch } = useFetch();
    const { openSnackbar } = useSnackbar();

    const {
        data: experimentMission,
        error: experimentMissionFetchError,
        isLoading: experimentMissionIsLoading,
    } = useSWR(
        experimentMissionUuid
            ? `${BACKEND_ROUTES.EXPERIMENT_MISSION}/${experimentMissionUuid}?parentInfo=true`
            : null
    );

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
    } = useForm({
        defaultValues: {
            processingStatus: "",
        },
    });

    useEffect(() => {
        if (experimentMission) {
            reset({
                processingStatus: experimentMission.processingStatus,
            });
        }
    }, [experimentMission, reset]);

    if (experimentMissionFetchError)
        return <FetchErrorAlert error={experimentMissionFetchError} />;

    if (experimentMissionIsLoading) {
        return (
            <Skeleton
                animation="wave"
                variant="rectangular"
                width="100%"
                height="30em"
            />
        );
    }

    const onSubmit = async (payload) => {
        const updatedMission = await patch(
            `${BACKEND_ROUTES.EXPERIMENT_MISSION}/${experimentMission.uuid}`,
            {
                body: payload,
            }
        );

        if (updatedMission) {
            openSnackbar(
                `Experiment Mission ${experimentMission.uuid} updated successfully.`,
                "success"
            );

            mutateTable();
            closeModal();
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <DialogTitle>
                {experimentMission.Experiment.Site.name} -{" "}
                {experimentMission.Experiment.name} -{" "}
                {formatDateTimeUTC(experimentMission.Mission.date)}
            </DialogTitle>
            <Divider variant="middle" />
            <DialogContent>
                <Grid container spacing={1}>
                    <Grid size={12}>
                        <Controller
                            control={control}
                            name="processingStatus"
                            render={({
                                field: { ref, onChange, ...fieldProps },
                            }) => (
                                <TextField
                                    {...fieldProps}
                                    fullWidth
                                    id="processingStatus"
                                    select
                                    label="Processing Status"
                                    inputRef={ref}
                                    error={Boolean(errors.processingStatus)}
                                    helperText={
                                        errors.processingStatus?.message
                                    }
                                    onChange={onChange}
                                >
                                    {processingStatusValues.map(
                                        (processingStatus) => (
                                            <MenuItem
                                                key={processingStatus}
                                                value={processingStatus}
                                            >
                                                {processingStatus}
                                            </MenuItem>
                                        )
                                    )}
                                </TextField>
                            )}
                            rules={{
                                required: INPUT_VALIDATION.REQUIRED,
                            }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button type="button" onClick={closeModal}>
                    Close
                </Button>
                <LoadingButton
                    type="submit"
                    variant="contained"
                    loading={isSubmitting}
                >
                    Update
                </LoadingButton>
            </DialogActions>
        </form>
    );
}
